import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useCart, classifyProduct } from '../context/CartContext';
import { ShoppingBag, Check } from 'lucide-react';

/**
 * Accessories – "Accesorios y otros" section.
 *
 * Uses a SECOND Booqable product-list embed. Visually hides any product whose
 * name does NOT look like an accessory (bolso, cartera, clutch, bag, handbag,
 * tacón, zapato, shoe, heel, arete, earring). Booqable script, pagination,
 * datepicker and availability are NEVER modified.
 *
 * Each visible accessory card gets a custom "Añadir al carrito" overlay
 * (consultation cart, NOT a checkout cart).
 */
const ACCESSORY_CATEGORIES = ['Todos', 'Bolsos', 'Tacones', 'Aretes'];

const KEYWORDS = {
  Bolsos: ['bolso', 'cartera', 'clutch', 'bag', 'handbag'],
  Tacones: ['tacón', 'tacon', 'tacones', 'zapato', 'zapatos', 'shoe', 'shoes', 'heel', 'heels'],
  Aretes: ['arete', 'aretes', 'earring', 'earrings'],
};

const ALL_ACCESSORY_KEYWORDS = [
  ...KEYWORDS.Bolsos,
  ...KEYWORDS.Tacones,
  ...KEYWORDS.Aretes,
];

const Accessories = () => {
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const sectionRef = useRef(null);
  const { addItem, items } = useCart();
  const [emptyPages, setEmptyPages] = useState(() => new Set());

  // Init Booqable
  useEffect(() => {
    const tryInit = () => {
      if (window.Booqable && typeof window.Booqable.init === 'function') {
        try { window.Booqable.init(); } catch (e) { /* noop */ }
      }
    };
    tryInit();
    const t = setTimeout(tryInit, 600);
    return () => clearTimeout(t);
  }, []);

  // Helper used by the indexer to force-tag the current page immediately.
  const tagCurrentPageNow = useCallback(() => {
    const section = sectionRef.current;
    if (!section) return 0;
    const list = section.querySelector('.booqable-product-list');
    if (!list) return 0;
    const activeKeywords =
      selectedCategory === 'Todos' ? ALL_ACCESSORY_KEYWORDS : KEYWORDS[selectedCategory] || [];
    let visibleCount = 0;
    list.querySelectorAll('.booqable-product').forEach((card) => {
      const txt = (card.textContent || '').slice(0, 300);
      if (txt.length < 3) return;
      const match = activeKeywords.some((kw) => txt.toLowerCase().includes(kw));
      if (match) {
        card.removeAttribute('data-acc-hidden');
        visibleCount++;
      } else {
        card.setAttribute('data-acc-hidden', 'true');
      }
    });
    return visibleCount;
  }, [selectedCategory]);

  // Index pages: when sub-filter is active, click through every Booqable page
  // and record which ones have at least one matching product. Pages with zero
  // matches get tagged so we hide them from the pagination control.
  useEffect(() => {
    if (selectedCategory === 'Todos') {
      setEmptyPages(new Set());
      return;
    }

    let cancelled = false;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    const getPageButtons = () => {
      const section = sectionRef.current;
      if (!section) return [];
      const list = section.querySelector('.booqable-product-list');
      if (!list) return [];
      return Array.from(list.querySelectorAll('.bq-pagination-page'))
        .map((wrapper) => {
          const btn = wrapper.querySelector('button');
          const n = parseInt((btn?.textContent || '').trim(), 10);
          return { wrapper, btn, n };
        })
        .filter((x) => !isNaN(x.n) && x.btn);
    };

    (async () => {
      // Wait for Booqable to render initial products + pagination
      await wait(1500);
      if (cancelled) return;

      const initial = getPageButtons();
      if (initial.length === 0) return; // no pagination = single page

      const pageNumbers = initial.map((p) => p.n).sort((a, b) => a - b);
      const empty = new Set();

      for (const n of pageNumbers) {
        if (cancelled) return;
        // Get fresh button reference (DOM may have re-rendered)
        const fresh = getPageButtons().find((p) => p.n === n);
        if (!fresh) continue;
        fresh.btn.click();
        await wait(800);
        if (cancelled) return;
        const visible = tagCurrentPageNow();
        if (visible === 0) empty.add(n);
      }
      if (cancelled) return;

      setEmptyPages(empty);

      // Navigate to first non-empty page so the user lands on visible results
      const firstOk = pageNumbers.find((n) => !empty.has(n));
      if (firstOk !== undefined) {
        const target = getPageButtons().find((p) => p.n === firstOk);
        if (target) target.btn.click();
      }
    })();

    return () => { cancelled = true; };
  }, [selectedCategory, tagCurrentPageNow]);

  // Re-apply data-empty-page tags to pagination buttons whenever Booqable
  // re-renders the pagination DOM (which it does on every page click).
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const applyTags = () => {
      section.querySelectorAll('.bq-pagination-page').forEach((wrapper) => {
        const btn = wrapper.querySelector('button');
        const n = parseInt((btn?.textContent || '').trim(), 10);
        if (!isNaN(n) && emptyPages.has(n)) {
          wrapper.setAttribute('data-empty-page', 'true');
        } else {
          wrapper.removeAttribute('data-empty-page');
        }
      });
    };

    applyTags();
    const obs = new MutationObserver(applyTags);
    obs.observe(section, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [emptyPages]);

  // Filter accessories vs non-accessories
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const activeKeywords =
      selectedCategory === 'Todos' ? ALL_ACCESSORY_KEYWORDS : KEYWORDS[selectedCategory] || [];

    const matchesAccessory = (text) => {
      const t = (text || '').toLowerCase();
      return activeKeywords.some((kw) => t.includes(kw));
    };

    const tagProducts = () => {
      const list = section.querySelector('.booqable-product-list');
      if (!list) return;
      const cards = list.querySelectorAll('.booqable-product');
      cards.forEach((card) => {
        const txt = (card.textContent || '').slice(0, 300);
        if (txt.length < 3) return;
        if (matchesAccessory(txt)) {
          card.removeAttribute('data-acc-hidden');
        } else {
          card.setAttribute('data-acc-hidden', 'true');
        }
      });
    };

    tagProducts();
    const obs = new MutationObserver(() => tagProducts());
    obs.observe(section, { childList: true, subtree: true, characterData: true });
    const interval = setInterval(tagProducts, 1200);
    return () => {
      obs.disconnect();
      clearInterval(interval);
    };
  }, [selectedCategory]);

  // Inject custom "Añadir al carrito" buttons over Booqable accessory cards.
  // We mount a React portal into each card so it stays in sync with cart state.
  const [portals, setPortals] = useState([]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const refreshPortals = () => {
      const list = section.querySelector('.booqable-product-list');
      if (!list) {
        setPortals((prev) => (prev.length === 0 ? prev : []));
        return;
      }
      const cards = Array.from(list.querySelectorAll('.booqable-product'));
      const next = [];
      cards.forEach((card) => {
        if (card.getAttribute('data-acc-hidden') === 'true') return;
        const name = extractName(card);
        if (!name) return;

        // Find/create a host node inside the card
        let host = card.querySelector(':scope > .morea-cart-host');
        if (!host) {
          host = document.createElement('div');
          host.className = 'morea-cart-host';
          card.appendChild(host);
        }
        next.push({ host, name, key: name });
      });
      setPortals((prev) => {
        if (prev.length === next.length && prev.every((p, i) => p.key === next[i].key)) {
          return prev;
        }
        return next;
      });
    };

    refreshPortals();
    const obs = new MutationObserver(refreshPortals);
    obs.observe(section, { childList: true, subtree: true });
    const interval = setInterval(refreshPortals, 1200);
    return () => {
      obs.disconnect();
      clearInterval(interval);
    };
  }, [selectedCategory]);

  const handleAdd = useCallback(
    (name) => {
      const type = classifyProduct(name, 'bolso');
      addItem({ name, type });
    },
    [addItem]
  );

  const isInCart = (name) =>
    items.some((i) => i.name.toLowerCase() === name.toLowerCase());

  return (
    <section id="accessories" className="py-20 bg-white" ref={sectionRef}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-serif text-neutral-900 mb-4">
            Accesorios y otros
          </h2>
          <p className="text-neutral-600 font-light max-w-2xl mx-auto">
            Bolsos, tacones y aretes con disponibilidad real. Agrégalos a tu
            carrito de consulta y envíalo por WhatsApp.
          </p>
        </div>

        <div className="mb-10">
          <div
            className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide justify-start sm:justify-center"
            data-testid="accessory-category-filters"
          >
            {ACCESSORY_CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`accessory-filter-${cat}`}
                onClick={() => setSelectedCategory(cat)}
                className={`whitespace-nowrap px-6 py-2.5 rounded-full text-sm font-light tracking-wide transition-all duration-300 border ${
                  selectedCategory === cat
                    ? 'bg-neutral-900 text-white border-neutral-900'
                    : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div
          className="booqable-embed-area accessories-list"
          data-testid="accessories-booqable"
          data-filter-active={selectedCategory !== 'Todos' ? 'true' : 'false'}
        >
          <div className="booqable-product-list"></div>
        </div>

        <p className="text-center text-neutral-500 font-light text-sm mt-12 max-w-2xl mx-auto">
          Disponibilidad confirmada por WhatsApp. Selecciona fechas en el catálogo de vestidos.
        </p>
      </div>

      {/* Render an "Añadir al carrito" button into each accessory card via portals */}
      {portals.map(({ host, name }) =>
        ReactDOM.createPortal(
          <AddToCartButton
            name={name}
            inCart={isInCart(name)}
            onAdd={() => handleAdd(name)}
          />,
          host
        )
      )}
    </section>
  );
};

function AddToCartButton({ name, inCart, onAdd }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!inCart) onAdd();
      }}
      data-testid={`acc-add-${slug(name)}`}
      className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs sm:text-sm py-2 rounded-sm transition-all duration-300 border ${
        inCart
          ? 'bg-neutral-100 text-neutral-700 border-neutral-200 cursor-default'
          : 'bg-neutral-900 hover:bg-neutral-800 text-white border-neutral-900'
      }`}
    >
      {inCart ? (
        <>
          <Check className="w-3.5 h-3.5" />
          En carrito
        </>
      ) : (
        <>
          <ShoppingBag className="w-3.5 h-3.5" />
          Añadir al carrito
        </>
      )}
    </button>
  );
}

function extractName(card) {
  // Try to find a clear product name node first
  const nameEl = card.querySelector('h2, h3, h4, h5, [class*="title" i], [class*="name" i]');
  if (nameEl) {
    return (nameEl.textContent || '').trim().split('\n')[0].slice(0, 80);
  }
  // Fallback: first textual line of the card minus any price line
  const raw = (card.textContent || '').trim();
  // Remove price segments like $10.00
  const cleaned = raw.replace(/\$[\d.,]+/g, '').trim();
  return cleaned.split('\n')[0].slice(0, 80);
}

function slug(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);
}

export default Accessories;
